/**
 * Loads every app tile on a /looker payload (plan 2026-10-12-looker-d1-phase3,
 * ruling 3): one run per (template, scope, period), each widget loaded once,
 * each tile failing on its own. In the master's all-workspaces scope nothing
 * is fetched — every app box asks for a workspace instead.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { APP_LOADERS, type AppLoaderContext } from '@/services/looker/lookerAppLoaders';
import type { AppTileData } from '@/services/looker/lookerAppData';
import type { AppWidget } from '@/services/looker/lookerAppTiles';
import type { LookerDashboardPayload } from '@/services/looker/lookerTypes';
import { logError } from '@/services/logger';

export type AppTileState =
  | { status: 'loading' }
  | { status: 'ready'; data: AppTileData }
  | { status: 'failed' }
  | { status: 'needsWorkspace' };

/**
 * A hidden-when-empty box (ruling 4): ready, and its data says it has nothing to
 * show. The grid, the chat context and the CSV all drop it by this one rule.
 */
export const isHiddenWhenEmpty = (state: AppTileState | undefined): boolean =>
  state?.status === 'ready' && state.data.hidden;

interface AppTileRef {
  id: string;
  widget: AppWidget;
}

const appTilesOf = (payload: LookerDashboardPayload | null): AppTileRef[] =>
  (payload?.tiles ?? []).flatMap((t) => (t.type === 'app' && t.app ? [{ id: t.id, widget: t.app.widget }] : []));

/**
 * What a page-level run depends on — not the payload object, which a channel
 * change replaces. Shared with useLookerBackfill so the two cannot drift.
 */
export const payloadRunKey = (payload: LookerDashboardPayload, language: string): string => {
  const scope = payload.scope.kind === 'all' ? 'all' : `ws:${payload.scope.workspaceId}`;
  const period = payload.period ? `${payload.period.start}..${payload.period.end}` : 'none';
  // The language too: labels and event sentences are built in the loaders.
  return [payload.template.id, scope, period, `lang:${language}`].join('|');
};

/**
 * Box 6's currency (R3): the forced or picked one, else the workspace's only one; null = several.
 * It deliberately IGNORES unknown-currency spend: box 6 excludes that spend and flags it (R4,
 * unknownCurrencyExcluded). Box 9/3's rule (api/_lib/lookerHandlers.ts, targetCurrency) does NOT
 * ignore it, because a Cost per Result cannot be judged with part of its spend unplaced (spec Q2).
 * Both are right; do not unify them (final review m8).
 */
const currencyOf = (p: LookerDashboardPayload): string | null =>
  p.currency ?? (p.currencies.length === 1 ? p.currencies[0] : null);

/** App tiles only (R7): a currency switch reloads box 6; backfill keeps payloadRunKey as is. */
const runKeyOf = (payload: LookerDashboardPayload | null, tiles: AppTileRef[], language: string): string => {
  if (!payload || tiles.length === 0) return '';
  const base = `${payloadRunKey(payload, language)}|cur:${currencyOf(payload) ?? ''}`;
  return [base, ...tiles.map((t) => `${t.id}=${t.widget}`)].join('|');
};

const allAs = (tiles: AppTileRef[], state: AppTileState): Record<string, AppTileState> =>
  Object.fromEntries(tiles.map((t) => [t.id, state]));

/** Runs each tile's loader, one call per widget, and settles every tile. */
const loadAll = async (tiles: AppTileRef[], ctx: AppLoaderContext): Promise<Record<string, AppTileState>> => {
  const byWidget = new Map<AppWidget, Promise<AppTileData>>();
  const load = (widget: AppWidget): Promise<AppTileData> => {
    const cached = byWidget.get(widget);
    if (cached) return cached;
    const started = Promise.resolve().then(() => APP_LOADERS[widget](ctx));
    byWidget.set(widget, started);
    return started;
  };
  const settled = await Promise.allSettled(tiles.map((t) => load(t.widget)));
  return Object.fromEntries(tiles.map((t, i) => {
    const r = settled[i];
    if (r.status === 'fulfilled') return [t.id, { status: 'ready', data: r.value } as const];
    logError('LookerAppTiles', `app tile ${t.widget} failed`, r.reason);
    return [t.id, { status: 'failed' } as const];
  }));
};

export const useLookerAppTiles = (payload: LookerDashboardPayload | null): Record<string, AppTileState> => {
  const { t, i18n } = useTranslation();
  const tiles = useMemo(() => appTilesOf(payload), [payload]);
  const runKey = runKeyOf(payload, tiles, i18n.language);
  const [result, setResult] = useState<{ key: string; states: Record<string, AppTileState> }>({ key: '', states: {} });

  // The run reads the latest payload and t, but starts only when runKey moves.
  const latest = useRef({ payload, tiles, t });
  latest.current = { payload, tiles, t };

  useEffect(() => {
    const { payload: p, tiles: current, t: tr } = latest.current;
    if (!runKey || !p || p.scope.kind === 'all') return;
    let ignore = false;
    const ctx: AppLoaderContext = {
      workspaceId: p.scope.workspaceId,
      period: p.period ? { start: p.period.start, end: p.period.end } : null,
      t: tr,
      now: new Date(),
      currency: currencyOf(p),
    };
    void loadAll(current, ctx).then((states) => {
      if (!ignore) setResult({ key: runKey, states });
    });
    return () => {
      ignore = true;
    };
  }, [runKey]);

  return useMemo(() => {
    if (!payload || tiles.length === 0) return {};
    if (payload.scope.kind === 'all') return allAs(tiles, { status: 'needsWorkspace' });
    return result.key === runKey ? result.states : allAs(tiles, { status: 'loading' });
  }, [payload, tiles, result, runKey]);
};
